// core/menu_builders.js
// Builds header + row context menus.
// NOTE: We keep this module "dumb": it only renders menu UI + calls provided ops.
// It does NOT own selection, row ops, column ops, totals, etc.

export function installMenuBuilders(ctx, deps) {
  if (!ctx) throw new Error("installMenuBuilders: ctx required");
  if (!deps) throw new Error("installMenuBuilders: deps required");

  const {
    // config/constants
    spec,
    rowMenuEnabled,
    ROW_HANDLE_KEY,

    // data/structure
    cols,
    colByKey,

    // utilities
    escCss,
    clampInt,
    isLockedCol,

    // formatting helpers
    isNumericCol,
    normalizeFormat,
    reformatAllNumeric,

    // dp overrides
    selectedNumericColKeys,
    applyDpToColumns,

    // operations (callbacks)
    autosizeColumns,
    unhideAllColumns,
    clearOutputsOnly,
    clearSelectedColumns,
    fillDownFromSelection,

    insertRowAbove,
    insertRowBelow,
    duplicateRows,
    deleteRows,
    clearSelectedRowsInputs,
    clearSelectedRowsOutputs,

    // row/table helpers
    getRowEls,
    setSelectedColumns,
    setSelectedRows,
    colIndexByKey,

    // clipboard helpers
    firstPasteableInputColIndex,
    copyRowsToTSV,
    pasteIntoRows,

    // messaging
    notify,
  } = deps;

  // ---------------------------------------------------------
  // Header menu
  // ---------------------------------------------------------
  ctx.buildHeaderMenu = function buildHeaderMenu() {
    return () => {
      const m = ctx.ensureMenu?.();
      if (!m) return;

      m.innerHTML = "";

      const mkItem = (label, fn, { danger = false, disabled = false, hint = "" } = {}) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.style.display = "block";
        b.style.width = "100%";
        b.style.textAlign = "left";
        b.style.padding = "8px 10px";
        b.style.borderRadius = "8px";
        b.style.border = "0";
        b.style.background = "transparent";
        b.style.color = "white";
        b.style.cursor = disabled ? "not-allowed" : "pointer";
        b.style.fontSize = "13px";
        b.style.opacity = disabled ? "0.45" : "1";
        b.title = hint || "";
        b.onmouseenter = () => {
          if (disabled) return;
          b.style.background = danger ? "rgba(255,80,80,0.18)" : "rgba(255,255,255,0.10)";
        };
        b.onmouseleave = () => (b.style.background = "transparent");
        b.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          ctx.hideMenu?.();
          fn?.();
        });
        return b;
      };

      const divider = () => {
        const d = document.createElement("div");
        d.style.cssText = "height:1px;background:rgba(255,255,255,0.12);margin:6px 4px;";
        return d;
      };

      const selKeys = ctx._sel?.selectedCols?.size ? [...ctx._sel.selectedCols] : [];
      const hasColSel = selKeys.length > 0;

      const allProtected =
        hasColSel &&
        selKeys.every((k) => {
          const c = colByKey.get(k);
          if (!c) return false;
          if (isLockedCol(c)) return true;
          if (c.kind === "input" && c.readonly) return true;
          return false;
        });

      // Decimals section (works for locked numeric columns too)
      const decKeys = selectedNumericColKeys();

      const getCurrentDp = (k) => {
        const col = colByKey.get(k);
        if (!col) return 4;
        const base = normalizeFormat(col).dp;
        const ov = ctx.getDpOverride?.(k);
        return ov != null ? ov : base;
      };

      const SCI_NOTATION_RE = /[eE][+-]?\d+/;

      const isScientificShown = (k) => {
        if (!k || typeof getRowEls !== "function") return false;
        const rows = getRowEls() || [];
        const maxScan = Math.min(rows.length, 48);

        for (let i = 0; i < maxScan; i += 1) {
          const tr = rows[i];
          if (!tr || tr.dataset?.teCompacted === "1") continue;

          const outEl = tr.querySelector?.(`[data-out="${escCss(k)}"]`);
          if (outEl) {
            const txt = String(outEl.textContent ?? "").trim();
            if (!txt || txt === "N/A") continue;
            return SCI_NOTATION_RE.test(txt);
          }

          const inEl = tr.querySelector?.(`[data-field="${escCss(k)}"]`);
          if (inEl) {
            const txt = String(inEl.value ?? "").trim();
            if (!txt) continue;
            return SCI_NOTATION_RE.test(txt);
          }
        }
        return false;
      };

      const currentDpLabel = (k) => {
        const dp = getCurrentDp(k);
        return isScientificShown(k) ? `${dp}, sci` : String(dp);
      };

      const applyDeltaDp = (delta) => {
        if (!decKeys.length) return;
        const cur = getCurrentDp(decKeys[0]);
        const next = clampInt(cur + delta, 0, 12);
        applyDpToColumns(decKeys, next);
      };

      const renderDecimalsSection = () => {
        if (!decKeys.length) return;

        const curDp = getCurrentDp(decKeys[0]);
        const curDpLabel = currentDpLabel(decKeys[0]);

        m.appendChild(divider());
        m.appendChild(
          mkItem(
            `Decimals… (${decKeys.length} col)`,
            null,
            {
              disabled: true,
              hint:
                "Use Increase/Decrease to adjust display decimals for the selected numeric columns. " +
                "When values are tiny, cells may display in scientific notation (sci).",
            },
          ),
        );

        m.appendChild(
          mkItem(
            `Increase decimals (+1)  (now ${curDpLabel})`,
            () => applyDeltaDp(+1),
            { hint: "Applies to all selected numeric columns (based on the first selected column)." },
          ),
        );

        m.appendChild(
          mkItem(
            `Decrease decimals (-1)  (now ${curDpLabel})`,
            () => applyDeltaDp(-1),
            { disabled: curDp <= 0, hint: "Applies to all selected numeric columns (based on the first selected column)." },
          ),
        );

        m.appendChild(divider());

        m.appendChild(
          mkItem(
            `Set decimals… (now ${curDpLabel})`,
            () => {
              const raw = prompt(
                `Set decimals for ${decKeys.length} selected numeric column(s).\n` +
                  `Enter an integer 0..12 (blank = reset to column default).\n` +
                  `Current (reference): ${curDpLabel}`,
                String(curDp),
              );

              if (raw == null) return;
              const s = String(raw).trim();
              if (s === "") {
                applyDpToColumns(decKeys, null);
                return;
              }
              const dp = clampInt(s, 0, 12);
              applyDpToColumns(decKeys, dp);
            },
            { hint: "This only changes display decimals; underlying precision is preserved." },
          ),
        );

        m.appendChild(mkItem("Reset decimals (use spec default)", () => applyDpToColumns(decKeys, null)));
      };

      // ----------------------------
      // Protected / read-only selection
      // ----------------------------
      if (allProtected) {
        m.appendChild(
          mkItem("Protected column", null, {
            disabled: true,
            hint: "This column is read-only/protected on this page.",
          })
        );

        m.appendChild(mkItem("Autosize selected column(s)", () => autosizeColumns(selKeys)));

        // Still allow decimals control for locked numeric columns
        renderDecimalsSection();

        m.appendChild(divider());
        m.appendChild(mkItem("Unhide all columns", () => unhideAllColumns()));
        if (typeof ctx.resetLayoutProfile === "function") {
          m.appendChild(
            mkItem("Reset saved layout", () => ctx.resetLayoutProfile(), {
              hint: "Restore default column order/width/visibility for this table.",
            }),
          );
        }
        m.appendChild(mkItem("Clear outputs", () => clearOutputsOnly(), { danger: true }));
        return;
      }

      // ----------------------------
      // Normal (editable) selection
      // ----------------------------
      const clearable = selKeys.filter((k) => {
        const c = colByKey.get(k);
        if (!c) return false;
        if (c.kind === "rowHandle") return false;
        if (isLockedCol(c)) return false;
        if (c.kind === "input" && c.readonly) return false;
        return c.kind === "input" || c.kind === "output";
      });

      const hidable = selKeys.filter((k) => {
        const c = colByKey.get(k);
        if (!c) return false;
        if (c.kind === "rowHandle") return false;
        if (isLockedCol(c)) return false;
        return true;
      });

      if (hasColSel) {
        m.appendChild(
          mkItem("Clear selected column(s)", () => clearSelectedColumns(), {
            danger: true,
            disabled: clearable.length === 0,
          }),
        );
        m.appendChild(
          mkItem(
            "Hide selected column(s)",
            () => {
              if (typeof ctx.setColumnsHidden === "function") {
                ctx.setColumnsHidden(hidable, true);
                return;
              }
              hidable.forEach((k) => ctx.setColumnHidden?.(k, true));
            },
            { disabled: hidable.length === 0 },
          ),
        );
        m.appendChild(mkItem("Autosize selected column(s)", () => autosizeColumns(selKeys)));
      } else {
        m.appendChild(mkItem("Clear outputs", () => clearOutputsOnly(), { danger: true }));
        m.appendChild(mkItem("Autosize all columns", () => autosizeColumns(null)));
      }

      renderDecimalsSection();

      m.appendChild(divider());
      m.appendChild(mkItem("Unhide all columns", () => unhideAllColumns()));
      if (typeof ctx.resetLayoutProfile === "function") {
        m.appendChild(
          mkItem("Reset saved layout", () => ctx.resetLayoutProfile(), {
            hint: "Restore default column order/width/visibility for this table.",
          }),
        );
      }
      m.appendChild(mkItem("Clear outputs", () => clearOutputsOnly(), { danger: true }));

      if (ctx._sel?.cellAnchor) {
        const col = cols[ctx._sel.cellAnchor.c];
        if (col && col.kind === "input" && !col.readonly && !isLockedCol(col)) {
          m.appendChild(divider());
          m.appendChild(mkItem("Fill down (from selected cell)", () => fillDownFromSelection()));
        }
      }
    };
  };

  // ---------------------------------------------------------
  // Row menu
  // ---------------------------------------------------------
  ctx.buildRowMenu = function buildRowMenu(contextRowIdx) {
    return () => {
      const m = ctx.ensureMenu?.();
      if (!m) return;

      m.innerHTML = "";

      const MIN_ROWS = Number.isFinite(spec?.minRows) ? spec.minRows : 0;

      const mkItem = (label, fn, { danger = false, disabled = false, hint = "" } = {}) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.style.display = "block";
        b.style.width = "100%";
        b.style.textAlign = "left";
        b.style.padding = "8px 10px";
        b.style.borderRadius = "8px";
        b.style.border = "0";
        b.style.background = "transparent";
        b.style.color = "white";
        b.style.cursor = disabled ? "not-allowed" : "pointer";
        b.style.fontSize = "13px";
        b.style.opacity = disabled ? "0.45" : "1";
        b.title = hint || "";

        b.onmouseenter = () => {
          if (disabled) return;
          b.style.background = danger ? "rgba(255,80,80,0.18)" : "rgba(255,255,255,0.10)";
        };
        b.onmouseleave = () => (b.style.background = "transparent");

        b.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          ctx.hideMenu?.();
          fn?.();
        });
        return b;
      };

      const divider = () => {
        const d = document.createElement("div");
        d.style.cssText = "height:1px;background:rgba(255,255,255,0.12);margin:6px 4px;";
        return d;
      };

      const rows = getRowEls();
      const nRows = rows.length;

      const hasSel = (ctx._sel?.selectedRows?.size || 0) > 0;
      const rowIdxs = hasSel ? [...ctx._sel.selectedRows].sort((a, b) => a - b) : [contextRowIdx];

      if (!hasSel || !ctx._sel.selectedRows.has(contextRowIdx)) {
        setSelectedRows([contextRowIdx], { anchorIdx: contextRowIdx });
      }

      // Robust delete feasibility
      const uniqueValidIdxs = [...new Set(rowIdxs)]
        .filter((i) => Number.isInteger(i))
        .filter((i) => i >= 0 && i < nRows);

      const requestedDeleteCount = uniqueValidIdxs.length || 0;
      const maxDeletable = MIN_ROWS > 0 ? Math.max(0, nRows - MIN_ROWS) : nRows;
      const canDelete = requestedDeleteCount > 0 && requestedDeleteCount <= maxDeletable;

      const deleteHint =
        !canDelete && MIN_ROWS > 0
          ? `Must keep at least ${MIN_ROWS} row(s).`
          : !canDelete
            ? "No rows selected."
            : "";

      m.appendChild(mkItem("Insert row above", () => insertRowAbove(contextRowIdx)));
      m.appendChild(mkItem("Insert row below", () => insertRowBelow(contextRowIdx)));

      m.appendChild(divider());
      m.appendChild(mkItem("Duplicate row(s)", () => duplicateRows(rowIdxs)));

      m.appendChild(
        mkItem(
          "Delete row(s)",
          () => deleteRows(rowIdxs),
          { danger: true, disabled: !canDelete, hint: deleteHint }
        )
      );

      m.appendChild(divider());
      m.appendChild(
        mkItem(
          "Clear row input cells",
          () => {
            if (!ctx._sel.selectedRows.size) setSelectedRows([contextRowIdx], { anchorIdx: contextRowIdx });
            clearSelectedRowsInputs();
          },
          { danger: true }
        )
      );

      m.appendChild(
        mkItem(
          "Clear row outputs",
          () => {
            if (!ctx._sel.selectedRows.size) setSelectedRows([contextRowIdx], { anchorIdx: contextRowIdx });
            clearSelectedRowsOutputs();
          },
          { danger: true }
        )
      );

      m.appendChild(divider());

      m.appendChild(
        mkItem("Copy row(s) (TSV)", async () => {
          const tsv = copyRowsToTSV(rowIdxs, { includeOutputs: false });
          if (!tsv) return;
          const ok = await ctx.clipboardWriteText?.(tsv);
          if (ok) {
            try { ctx.captureCopySourceFromSelection?.(); } catch {}
          }
          ok ? notify?.ok?.("Copied rows.") : notify?.err?.("Copy failed.");
        })
      );

      m.appendChild(
        mkItem("Copy row(s) incl outputs (TSV)", async () => {
          const tsv = copyRowsToTSV(rowIdxs, { includeOutputs: true });
          if (!tsv) return;
          const ok = await ctx.clipboardWriteText?.(tsv);
          if (ok) {
            try { ctx.captureCopySourceFromSelection?.(); } catch {}
          }
          ok ? notify?.ok?.("Copied rows (incl outputs).") : notify?.err?.("Copy failed.");
        })
      );

      const canPaste = firstPasteableInputColIndex() >= 0;
      m.appendChild(
        mkItem(
          "Paste into row(s)",
          async () => {
            const start = rowIdxs.length ? rowIdxs[0] : contextRowIdx;
            await pasteIntoRows(start);
          },
          { disabled: !canPaste }
        )
      );
    };
  };
}
